import { skipToken, useQuery } from '@tanstack/react-query'

import type { Product, ProductDerivedFields, ProductWithStock } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

const STOCK_TTL_MS = 30_000

/**
 * Fetches a single product by ID. When include is 'stock', the product includes
 * per-clinic stock rows, kept fresh with a short TTL and a background poll. Pass
 * a nullish `productId` to skip the fetch — the query stays inactive and `data`
 * is `undefined`.
 */
export function useGetProduct(
  productId: string | null | undefined,
  options: { include: 'stock' }
): ReturnType<typeof useQuery<ProductWithStock>>

export function useGetProduct(
  productId: string | null | undefined,
  options?: { include?: undefined }
): ReturnType<typeof useQuery<Product & ProductDerivedFields>>

export function useGetProduct(
  productId: string | null | undefined,
  options?: { include?: 'stock' }
) {
  const api = useOsApi()
  const include = options?.include

  return useQuery({
    queryKey: ['product', productId ?? null, { include }],
    queryFn: productId
      ? async () => {
          const response = await api.get('/products/:id', {
            params: { id: productId },
            query: { include },
          })
          if (!response.success) throw new Error(response.error.message)
          return response.data.product
        }
      : skipToken,
    staleTime: include === 'stock' ? STOCK_TTL_MS : undefined,
    refetchInterval: include === 'stock' ? 2 * STOCK_TTL_MS : undefined,
  })
}
